import React, { useCallback, useEffect, useState, Fragment } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import { dashboardToast } from '../../utils/dashboardToast';
import 'bootstrap/dist/css/bootstrap.min.css';
import Menu from '../components/menu';
import DashboardShell from '../../components/DashboardShell';
import { useMenuState } from "../../Common/MenuStateContext";
import SaListPageShell from '../../components/sa/SaListPageShell';
import ContentLoader from '../../components/ContentLoader';
import RecordDetailModal from '../../components/RecordDetailModal';
import CatalogFilterChip from "../../components/sa/CatalogFilterChip";
import TableSortSelect from '../../components/TableSortSelect';
import { Alert, Button as UiButton, Pagination as UiPagination } from '../../components/ui';
import { TABLE_SORT } from '../../utils/tableSort';
import {
  approveRejectProjectSetupRequest,
  getProjectSetupRequests,
  ProjectSetupRequestItem,
} from '../../services/projectSetupRequestApi';
import { notifySaPendingApprovalsChanged } from '../../utils/saPendingApprovals';
import { Building2, Check, ClipboardList, Mail, Network, Pencil, Search, X } from "lucide-react";
import '../../assets/css/dashboard.css';

const getStatusClass = (status: string) => {
  switch (status) {
    case 'Approved':
      return 'app-status app-status--success';
    case 'Rejected':
      return 'app-status app-status--danger';
    case 'Pending':
      return 'app-status app-status--warning';
    default:
      return 'app-status app-status--muted';
  }
};

const StatusPill = ({ status }: { status: string }) => (
  <span className={getStatusClass(status)}>{status}</span>
);

const EmptyCell = () => <span className="app-td__empty">—</span>;

const formatDate = (value?: string) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const ProjectSetupApproval = () => {
  const { isMenuCollapsed, toggleMenuCollapse } = useMenuState();
  const [selectedItem, setSelectedItem] = useState(null);
  const [requests, setRequests] = useState<ProjectSetupRequestItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortKey, setSortKey] = useState(TABLE_SORT.DATE_DESC);
  const [selectedRequest, setSelectedRequest] = useState<ProjectSetupRequestItem | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewRequest, setViewRequest] = useState<ProjectSetupRequestItem | null>(null);
  const [adminComment, setAdminComment] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [processingId, setProcessingId] = useState<number | null>(null);

  const [apiPageInfo, setApiPageInfo] = useState({
    pageNumber: 1,
    pageSize: 10,
    totalPages: 1,
    totalRecords: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  const handleMenuSelection = useCallback((item) => {
    setSelectedItem(item);
    return item;
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getProjectSetupRequests(
        debouncedSearch,
        statusFilter || 'All',
        apiPageInfo.pageNumber,
        apiPageInfo.pageSize,
        sortKey
      );
      setRequests(result.data);
      setApiPageInfo((prev) => ({
        ...prev,
        totalRecords: result.totalRecords,
        totalPages: result.totalPages,
        hasNextPage: result.hasNextPage,
        hasPreviousPage: result.hasPreviousPage,
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch project setup requests';
      setError(message);
      dashboardToast.error('Failed to fetch project setup requests');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, statusFilter, apiPageInfo.pageNumber, apiPageInfo.pageSize, sortKey]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setApiPageInfo((prev) => ({ ...prev, pageNumber: 1 }));
  }, []);

  const handleStatusFilterChange = useCallback((nextStatus: string) => {
    setStatusFilter(nextStatus);
    setApiPageInfo((prev) => ({ ...prev, pageNumber: 1 }));
  }, []);

  const handleSortChange = useCallback((nextSort: string) => {
    setSortKey(nextSort);
    setApiPageInfo((prev) => ({ ...prev, pageNumber: 1 }));
  }, []);

  const openDetails = (request: ProjectSetupRequestItem) => {
    setSelectedRequest(request);
    setAdminComment(request.adminComment || '');
    setRejectionReason('');
    setShowDetailModal(true);
  };

  const closeDetails = () => {
    setShowDetailModal(false);
    setSelectedRequest(null);
    setAdminComment('');
    setRejectionReason('');
  };

  const openViewModal = (request: ProjectSetupRequestItem) => {
    setViewRequest(request);
    setShowViewModal(true);
  };

  const closeViewModal = () => {
    setShowViewModal(false);
    setViewRequest(null);
  };

  const handleAction = async (action: 'Approve' | 'Reject') => {
    if (!selectedRequest) return;

    if (action === 'Reject' && !rejectionReason.trim()) {
      dashboardToast.error('Rejection reason is required.');
      return;
    }

    setProcessingId(selectedRequest.id);
    try {
      await approveRejectProjectSetupRequest({
        id: selectedRequest.id,
        action,
        adminComment: adminComment.trim() || undefined,
        rejectionReason: action === 'Reject' ? rejectionReason.trim() : undefined,
      });
      dashboardToast.success(`Request ${action.toLowerCase()}d successfully.`);
      closeDetails();
      fetchRequests();
      notifySaPendingApprovalsChanged();
    } catch (err) {
      dashboardToast.error(err instanceof Error ? err.message : 'Failed to process request');
    } finally {
      setProcessingId(null);
    }
  };

  const goToPage = useCallback(
    (page: number) => {
      const pg = Math.min(Math.max(page, 1), apiPageInfo.totalPages);
      if (pg !== apiPageInfo.pageNumber) {
        setApiPageInfo((prev) => ({ ...prev, pageNumber: pg }));
      }
    },
    [apiPageInfo.pageNumber, apiPageInfo.totalPages]
  );

  return (
    <DashboardShell
      isMenuCollapsed={isMenuCollapsed}
      sidebar={
        <Menu
          onSelect={handleMenuSelection}
          selectedItem={selectedItem}
          isCollapsed={isMenuCollapsed}
          onToggleCollapse={toggleMenuCollapse}
        />
      }
    >
      <SaListPageShell topBarTitle="Project Setup Requests">
        <div className="dash-frame-parent1">
          <div className="dash-list-toolbar">
            <div className="dash-list-toolbar__intro">
              <h5 className="dash-list-toolbar__title">Project Setup Requests</h5>
              <p className="dash-list-toolbar__desc">
                Review public requests to create a company and project.
              </p>
            </div>
            <div className="dash-list-toolbar__actions">
              <div className="dash-search-field dash-table-search">
                <Search className="dash-search-field__icon" aria-hidden />
                <input
                  type="text"
                  placeholder="Search email, company, project…"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="dash-search-field__input"
                  disabled={loading}
                />
              </div>
              <CatalogFilterChip
                label="Status"
                value={statusFilter === "All" ? "" : statusFilter}
                onChange={(value) => handleStatusFilterChange(value || "All")}
                disabled={loading}
                options={[
                  { value: "Pending", label: "Pending" },
                  { value: "Approved", label: "Approved" },
                  { value: "Rejected", label: "Rejected" },
                ]}
              />
              <TableSortSelect variant="chip" value={sortKey} onChange={handleSortChange} disabled={loading} />
            </div>
          </div>

          {loading ? (
            <ContentLoader label="Loading project setup requests…" />
          ) : error ? (
            <Alert variant="error">{error}</Alert>
          ) : (
            <Fragment>
              <div className="app-table-wrap app-table-wrap--refined">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th className="app-th--num">ID</th>
                      <th>Requested</th>
                      <th>Requester</th>
                      <th>Company</th>
                      <th>Industry</th>
                      <th>Project</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {requests.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="text-center">
                          <span className="app-td__desc">No project setup requests found.</span>
                        </td>
                      </tr>
                    ) : (
                      requests.map((request) => (
                        <tr
                          key={request.id}
                          className="rdm-row-clickable"
                          onClick={() => openViewModal(request)}
                        >
                          <td className="app-td--num">
                            <span className="app-td__index">{request.id}</span>
                          </td>
                          <td>
                            <span className="app-td__meta">{formatDate(request.requestDate)}</span>
                          </td>
                          <td>
                            <span className="app-td__title">{request.requesterEmail}</span>
                          </td>
                          <td>
                            <span className="app-td__desc">{request.companyName}</span>
                          </td>
                          <td>
                            {request.industry ? (
                              <span className="app-td__desc" title={request.industry}>
                                {request.industry}
                              </span>
                            ) : (
                              <EmptyCell />
                            )}
                          </td>
                          <td>
                            <span className="app-td__desc">{request.projectName}</span>
                          </td>
                          <td>
                            <StatusPill status={request.status} />
                          </td>
                          <td
                            className="dash-mobile-table-actions"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="btn-list-actions">
                              <UiButton
                                variant="outline"
                                size="sm"
                                className="btn-list-edit"
                                onClick={() => openDetails(request)}
                                title={
                                  request.status === 'Pending'
                                    ? 'Review request'
                                    : 'View request details'
                                }
                                aria-label={
                                  request.status === 'Pending'
                                    ? `Review ${request.companyName}`
                                    : `View ${request.companyName}`
                                }
                              >
                                <Pencil />
                              </UiButton>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <UiPagination
                page={apiPageInfo.pageNumber}
                totalPages={apiPageInfo.totalPages}
                onPageChange={goToPage}
              />
            </Fragment>
          )}
        </div>
      </SaListPageShell>

      <Modal
        show={showDetailModal}
        onHide={closeDetails}
        centered
        size="lg"
        backdrop="static"
        enforceFocus
        restoreFocus
        dialogClassName="psr-setup-review-dialog rounded-3"
        contentClassName="psr-setup-review-modal"
      >
        <Modal.Header closeButton className="dashboard-modal-header">
          <ClipboardList className="h-5 w-5 me-2" />
          <Modal.Title>Review Project Setup Request</Modal.Title>
        </Modal.Header>

        <Modal.Body className="px-4 psr-setup-review-body">
          {selectedRequest && (
            <>
              <p className="cap-mgmt-section-label mb-3">
                <Mail className="me-2" />
                Requester
              </p>
              <Row className="mb-4">
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Work email</Form.Label>
                    <Form.Control type="text" value={selectedRequest.requesterEmail} readOnly />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Requested on</Form.Label>
                    <Form.Control type="text" value={formatDate(selectedRequest.requestDate)} readOnly />
                  </Form.Group>
                </Col>
              </Row>

              <p className="cap-mgmt-section-label mb-3">
                <Building2 className="me-2" />
                Company details
              </p>
              <Row className="mb-4">
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Company name</Form.Label>
                    <Form.Control type="text" value={selectedRequest.companyName} readOnly />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Company info</Form.Label>
                    <Form.Control type="text" value={selectedRequest.companyInfo} readOnly />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Contact info</Form.Label>
                    <Form.Control type="text" value={selectedRequest.companyContactInfo} readOnly />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Industry</Form.Label>
                    <Form.Control type="text" value={selectedRequest.industry || '—'} readOnly />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Company size</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={selectedRequest.companySize || '—'}
                      readOnly
                    />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Engagement goal</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={selectedRequest.engagementGoal || '—'}
                      readOnly
                    />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Additional context</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={selectedRequest.additionalContext || '—'}
                      readOnly
                    />
                  </Form.Group>
                </Col>
              </Row>

              <p className="cap-mgmt-section-label mb-3">
                <Network className="me-2" />
                Project details
              </p>
              <Row className="mb-4">
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Project name</Form.Label>
                    <Form.Control type="text" value={selectedRequest.projectName} readOnly />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Department</Form.Label>
                    <Form.Control type="text" value={selectedRequest.departmentName} readOnly />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Creation date</Form.Label>
                    <Form.Control type="text" value={selectedRequest.projectCreationDate} readOnly />
                  </Form.Group>
                </Col>
              </Row>

              {selectedRequest.status === 'Pending' ? (
                <>
                  <p className="cap-mgmt-section-label mb-3">Review decision</p>
                  <Row>
                    <Col md={12}>
                      <Form.Group className="mb-3">
                        <Form.Label>Admin comment (optional)</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          value={adminComment}
                          onChange={(e) => setAdminComment(e.target.value)}
                          placeholder="Enter a comment..."
                          maxLength={500}
                        />
                        <Form.Text className="text-muted">
                          {adminComment.length}/500 characters
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={12}>
                      <Form.Group className="mb-3">
                        <Form.Label className="required">Rejection reason</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          value={rejectionReason}
                          onChange={(e) => setRejectionReason(e.target.value)}
                          placeholder="Enter rejection reason if rejecting this request..."
                          maxLength={500}
                        />
                        <Form.Text className="text-muted">
                          {rejectionReason.length}/500 characters
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                </>
              ) : (
                <>
                  <p className="cap-mgmt-section-label mb-3">Review outcome</p>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Status</Form.Label>
                        <div>
                          <StatusPill status={selectedRequest.status} />
                        </div>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Reviewed</Form.Label>
                        <Form.Control
                          type="text"
                          value={
                            selectedRequest.reviewedAt
                              ? `${formatDate(selectedRequest.reviewedAt)}${
                                  selectedRequest.reviewedByName
                                    ? ` by ${selectedRequest.reviewedByName}`
                                    : ''
                                }`
                              : '—'
                          }
                          readOnly
                        />
                      </Form.Group>
                    </Col>
                    {selectedRequest.status === 'Approved' ? (
                      <Col md={12}>
                        <p className="psr-setup-review-outcome text-muted mb-3">
                          <strong>{selectedRequest.companyName}</strong> and{' '}
                          <strong>{selectedRequest.projectName}</strong> have been created.
                          Manage them from Company Management and Project Management.
                        </p>
                      </Col>
                    ) : null}
                    {selectedRequest.rejectionReason ? (
                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label>Rejection reason</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={2}
                            value={selectedRequest.rejectionReason}
                            readOnly
                          />
                        </Form.Group>
                      </Col>
                    ) : null}
                    {selectedRequest.adminComment ? (
                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label>Admin comment</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={2}
                            value={selectedRequest.adminComment}
                            readOnly
                          />
                        </Form.Group>
                      </Col>
                    ) : null}
                  </Row>
                </>
              )}
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={closeDetails}
            style={{ width: 'auto' }}
          >
            Close
          </Button>
          {selectedRequest?.status === 'Pending' && (
            <>
              <Button
                variant="danger"
                onClick={() => handleAction('Reject')}
                disabled={processingId === selectedRequest.id}
                style={{ width: 'auto' }}
              >
                <X className="me-1" />
                Reject
              </Button>
              <Button
                className="btn btn-dashboard-primary"
                onClick={() => handleAction('Approve')}
                disabled={processingId === selectedRequest.id}
                style={{ width: 'auto' }}
              >
                <Check className="me-1" />
                Approve &amp; Invite
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>

      <RecordDetailModal
        show={showViewModal}
        onHide={closeViewModal}
        title={viewRequest?.projectName || 'Project Setup Request'}
        subtitle={
          viewRequest
            ? `${viewRequest.companyName} · ${viewRequest.requesterEmail}`
            : 'Project setup request'
        }
        size="xl"
        groups={[
          {
            title: 'Requester',
            fields: [
              { label: 'Work email', value: viewRequest?.requesterEmail },
              {
                label: 'Requested on',
                value: formatDate(viewRequest?.requestDate),
              },
            ],
          },
          {
            title: 'Company details',
            fields: [
              { label: 'Company name', value: viewRequest?.companyName },
              { label: 'Company info', value: viewRequest?.companyInfo },
              {
                label: 'Contact info',
                value: viewRequest?.companyContactInfo,
              },
              { label: 'Industry', value: viewRequest?.industry },
              {
                label: 'Company size',
                value: viewRequest?.companySize,
                fullWidth: true,
              },
              {
                label: 'Engagement goal',
                value: viewRequest?.engagementGoal,
                fullWidth: true,
              },
              {
                label: 'Additional context',
                value: viewRequest?.additionalContext,
                fullWidth: true,
              },
            ],
          },
          {
            title: 'Project details',
            fields: [
              { label: 'Project name', value: viewRequest?.projectName },
              { label: 'Department', value: viewRequest?.departmentName },
              {
                label: 'Creation date',
                value: viewRequest?.projectCreationDate,
              },
            ],
          },
          {
            title: 'Review status',
            fields: [
              {
                label: 'Status',
                value: viewRequest
                  ? <StatusPill status={viewRequest.status} />
                  : undefined,
              },
              {
                label: 'Reviewed',
                value: viewRequest?.reviewedAt
                  ? `${formatDate(viewRequest.reviewedAt)}${
                      viewRequest.reviewedByName
                        ? ` by ${viewRequest.reviewedByName}`
                        : ''
                    }`
                  : undefined,
              },
              ...(viewRequest?.rejectionReason
                ? [
                    {
                      label: 'Rejection reason',
                      value: viewRequest.rejectionReason,
                      fullWidth: true,
                    },
                  ]
                : []),
              ...(viewRequest?.adminComment
                ? [
                    {
                      label: 'Admin comment',
                      value: viewRequest.adminComment,
                      fullWidth: true,
                    },
                  ]
                : []),
            ],
          },
        ]}
        actions={[
          {
            type: 'review',
            label:
              viewRequest?.status === 'Pending' ? 'Review' : 'Details',
            onClick: () => {
              const request = viewRequest;
              closeViewModal();
              if (request) openDetails(request);
            },
          },
        ]}
      />

    </DashboardShell>
  );
};

export default ProjectSetupApproval;
